import React from "react"
import { t, Trans } from "@lingui/macro"
import { i18n } from "@lingui/core"
import {
    ButtonsContainer,
    isSanityImagePresent,
    ReadMore,
    ResponsiveSanityImage,
    SanityImageRatio,
    Tab,
    TabList,
    TabPanel,
    Tabs,
} from "@silversea-ssc/ui-components-react"
import { Amount, AvailabilityType, SuitePrice } from "@ssc-booking/api-contracts"
import { SUITE_FEATURE_GROUPINGS } from "@silversea-ssc/web-suite-features"

import {
    SanityShipComparableFeatures,
    SanityShipSections,
    SanitySuiteModalQueryResponse,
} from "../../app-core/_common/sanity/sanity-types"
import BlockContent from "../BlockContent/BlockContent"
import { ResponsiveSlideshow } from "../Slideshow/Slideshow"
import { useGeolocation } from "../../app-core/_common/providers/geolocation-provider"
import LocalizedShipDeckPlansSlider, {
    CompletedShipDeckPlanSlideProps,
} from "../LocalizedShipDeckPlansSlider/LocalizedShipDeckPlansSlider"
import { SuiteContentWithConfiguration } from "../../app-core/v3/_common/read-models/suite-category"
import GuaranteedSuiteInfo from "../GuaranteedSuiteInfo/GuaranteedSuiteInfo"
import FormattedPricesBlock from "../Prices/FormattedPricesBlock"
import styles from "./SuiteModal.module.scss"
import { SuiteModalSelectButton } from "./SuiteModalSelectButton"

interface Props {
    title: string
    suite: SuiteContentWithConfiguration<SanitySuiteModalQueryResponse["version"]["suites"][0]>
    price?: SuitePrice
    isCurrentSuite: boolean
    suiteCategory: string
    deckPlans?: CompletedShipDeckPlanSlideProps[]
    totalShipDecks?: number
    availability: AvailabilityType
    SuiteModalSelectButton: SuiteModalSelectButton
}

const mapFeatureGroupings = (comparableFeatures: SanityShipComparableFeatures) =>
    SUITE_FEATURE_GROUPINGS.map((grouping) => ({
        title: grouping.title,
        features: grouping.features
            .map((feature) => ({
                ...feature,
                value: comparableFeatures[feature.key],
            }))
            .filter((x) => x.value)
            .map((feature) => {
                if (feature.type === "boolean") return i18n._(feature.key)
                if (feature.type === "options") return i18n._(feature.value)
            }),
    }))

export const getSectionKeys = (sections: SanityShipSections): string[] => {
    const result: string[] = []
    if (sections.aft) result.push("shipSection_aft")
    if (sections.forward) result.push("shipSection_forward")
    if (sections.midShip) result.push("shipSection_midShip")
    if (sections.port) result.push("shipSection_port")
    if (sections.starboard) result.push("shipSection_starboard")
    return result
}

const SuiteModalContent: React.FC<React.PropsWithChildren<Props>> = ({
    title,
    suite,
    price,
    isCurrentSuite,
    suiteCategory,
    deckPlans,
    totalShipDecks,
    availability,
    SuiteModalSelectButton,
}) => {
    const description = suite?.description
    const images = suite?.images?.filter((i) => !!i)
    const totalAmount = price?.total
    const groupings = mapFeatureGroupings(suite.comparableFeatures)
    const geoLocation = useGeolocation()
    const configuration = suite.configuration
    const useFeet = geoLocation.unit === "feet"

    const shortSizeDescription = useFeet
        ? configuration?.shortSizeDescriptionInSquareFeet
        : configuration?.shortSizeDescriptionInSquareMetres

    const verandaSizeDescription = useFeet
        ? configuration?.verandaSizeInSquareFeet
        : configuration?.verandaSizeInSquareMetres

    return (
        <div className={styles.container}>
            <div className={styles.leftSide}>
                <Tabs data-content-name="tabs">
                    <div className={styles.tabListBackground}>
                        <TabList wrapperClassName={styles.tabList}>
                            <Tab noWrap underline data-content-piece={"suite-overview"}>
                                <Trans>Suite Overview</Trans>
                            </Tab>
                            <Tab noWrap underline data-content-piece={"suite-plan"}>
                                <Trans>Suite Plan</Trans>
                            </Tab>
                            <Tab noWrap underline data-content-piece={"suite-features"}>
                                <Trans>Suite Features</Trans>
                            </Tab>
                            {deckPlans && deckPlans.length > 0 && (
                                <Tab noWrap underline data-content-piece={"suite-location"}>
                                    <Trans>Suite Location</Trans>
                                </Tab>
                            )}
                        </TabList>
                    </div>
                    <TabPanel spacingTop className={styles.tabPanel} data-content-name="tab_content_overview">
                        {images && (
                            <ResponsiveSlideshow items={images} variant="gray" fullscreen={false} hideCaption={true} />
                        )}
                    </TabPanel>
                    <TabPanel spacingTop className={styles.tabPanel} data-content-name="tab_content_plan">
                        {configuration?.plan && isSanityImagePresent(configuration.plan) && (
                            <SanityImageRatio ratio="auto" image={configuration.plan}>
                                <ResponsiveSanityImage
                                    image={configuration.plan}
                                    className={styles.suitePlan}
                                    defaultWidth={600}
                                    defaultHeight={500}
                                    fit="original"
                                />
                            </SanityImageRatio>
                        )}
                    </TabPanel>
                    <TabPanel spacingTop className={styles.tabPanel} data-content-name="tab_content_features">
                        <ReadMore
                            height="6.5em"
                            backgroundColor="white"
                            openLabel={<Trans>Expand Info</Trans>}
                            closeLabel={<Trans>Reduce Info</Trans>}
                        >
                            <div className={styles.suiteFeatures}>
                                <div className={styles.group}>
                                    <h3>{i18n._(t`suite_essentials`)}</h3>
                                    <ul>
                                        <li>
                                            <span>
                                                {i18n._(t`suite_deckS`)}: {suite.deck?.join(", ")}
                                            </span>
                                        </li>
                                        {suite.shipSections && (
                                            <li>
                                                <span>
                                                    {i18n._(t`suite_shipSection`)}:{" "}
                                                    {getSectionKeys(suite.shipSections)
                                                        .map((key) => i18n._(key))
                                                        .join(", ")}
                                                </span>
                                            </li>
                                        )}
                                    </ul>
                                </div>
                                {groupings.map((grouping) => {
                                    return (
                                        <div key={grouping.title} className={styles.group}>
                                            <h3>{grouping.title}</h3>
                                            <ul>
                                                {grouping.features.map((feature) => (
                                                    <li key={feature}>
                                                        <span>{feature}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )
                                })}
                            </div>
                        </ReadMore>
                    </TabPanel>
                    {deckPlans && deckPlans.length > 0 && (
                        <TabPanel spacingTop className={styles.tabPanel} data-content-name="tab_content_location">
                            <div className={styles.deckPlanSlider}>
                                <LocalizedShipDeckPlansSlider
                                    deckPlans={deckPlans}
                                    totalDecksNumber={totalShipDecks || 0}
                                    variant="complete"
                                />
                            </div>
                        </TabPanel>
                    )}
                </Tabs>
            </div>
            <div className={styles.rightSide}>
                <h3>{title}</h3>
                {availability === "guaranteed" && (
                    <span className={styles.guaranteed}>
                        <GuaranteedSuiteInfo className={styles.guaranteedLabel} />
                    </span>
                )}
                <BlockContent value={description} />
                {configuration && (
                    <div className={styles.bedroomsInfo} key={configuration?.code}>
                        <p>
                            {configuration.name}: {shortSizeDescription}{" "}
                            {verandaSizeDescription ? `(${verandaSizeDescription})` : ""}
                        </p>
                        <BlockContent value={configuration?.configurationInformation} />
                    </div>
                )}
                {totalAmount && !Amount.isZero(totalAmount) && (
                    <div className={styles.ctaContainer} data-content-name="price">
                        <FormattedPricesBlock price={price} />

                        <ButtonsContainer mobileFullSize align="left" spacingTop="regular" gap="compact">
                            <SuiteModalSelectButton
                                selected={isCurrentSuite}
                                suiteCategory={suiteCategory}
                                availability={availability}
                            />
                        </ButtonsContainer>
                    </div>
                )}
            </div>
        </div>
    )
}

export default SuiteModalContent
